// src/pages/dashboards/company/CompanyOverview.jsx
// Landing page for the company portal. Hero scan-pool panel, centered KPI strip,
// rich claims insights (approval rate, avg payout time, value by month, top
// claimers, avg value + MoM trend), donut, and company details - all on glass.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as companyApi from "../../../api/company";
import { Loading } from "../../../components/ui";
import { GaugeChart, DonutChart, BarChart } from "../../../components/charts";
import { Hourglass, Banknote, BadgeCheck, TrendingUp, UserGroup } from "../../../components/icons";
import { money } from "../../../lib/format";
import { useToast } from "../../../context/ToastContext";

const STATUS_COLORS = {
  submitted: "#3B82F6",
  queried: "#F59E0B",
  paid: "#12B266",
  rejected: "#EF4444",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// glass surface wrapper
const Glass = ({ className = "", children }) => (
  <div className={`glass rounded-2xl border border-white/20 dark:border-white/5 shadow-xl shadow-black/[0.04] dark:shadow-black/30 ${className}`}>
    {children}
  </div>
);

// centered KPI tile with gradient icon badge; clickable when onClick is given
// lighten a hex toward white for the gradient top-stop
const lift = (hex, t = 0.45) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const mix = (c) => Math.round(parseInt(c, 16) + (255 - parseInt(c, 16)) * t);
  return `rgb(${mix(m[1])}, ${mix(m[2])}, ${mix(m[3])})`;
};

const Kpi = ({ icon: Icon, tint = "#12B266", label, value, valueColor = "", sub, onClick }) => {
  const inner = (
    <Glass className="p-5 h-full flex flex-col items-center text-center group">
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mb-3 transition-transform group-hover:scale-105"
        style={{
          background: `linear-gradient(135deg, ${lift(tint)}, ${tint})`,
          boxShadow: `0 4px 12px ${tint}66`,
        }}
      >
        <Icon size={17} color="#ffffff" />
      </div>
      <p className="text-xs font-semibold text-tb-muted uppercase tracking-wider mb-2">{label}</p>
      <p className={`text-2xl font-bold leading-none ${valueColor}`}>{value}</p>
      {sub && <p className="text-sm text-tb-muted mt-2">{sub}</p>}
    </Glass>
  );
  if (!onClick) return inner;
  return (
    <button onClick={onClick} className="block w-full text-left transition-transform hover:-translate-y-0.5">
      {inner}
    </button>
  );
};

// last-12-months skeleton keyed by year-month
const monthBuckets = () => {
  const now = new Date();
  const out = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: MONTHS[d.getMonth()], count: 0, value: 0 });
  }
  return out;
};

export default function CompanyOverview() {
  const toast = useToast();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [stats, setStats] = useState(null);
  const [teamCount, setTeamCount] = useState(null);
  const [members, setMembers] = useState([]);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    companyApi.getCompany().then(setCompany).catch((e) => toast.error(e.message));
    companyApi.linkedUsers()
      .then((u) => { setTeamCount(u.length); setMembers(u); })
      .catch(() => { setTeamCount(0); setMembers([]); });

    Promise.all(
      ["submitted", "queried", "paid", "rejected"].map((s) =>
        companyApi.listClaims(s)
          .then((rows) => ({ status: s, rows, count: rows.length, total: rows.reduce((sum, c) => sum + Number(c.total || 0), 0) }))
          .catch(() => ({ status: s, rows: [], count: 0, total: 0 }))
      )
    ).then((arr) => {
      const map = {};
      const all = [];
      arr.forEach((a) => { map[a.status] = a; all.push(...a.rows); });
      setStats(map);
      setInsights(computeInsights(map, all));
    });
  }, []);

  if (!company || !stats || !insights) return <Loading label="Loading dashboard" />;

  const pool = company.pool || {};
  const unlimited = pool.limit == null;
  const remainPct = unlimited ? 1 : pool.limit > 0 ? pool.remaining / pool.limit : 0;
  const gaugeColor = remainPct < 0.15 ? "#EF4444" : remainPct < 0.35 ? "#F59E0B" : "#12B266";
  const usedPct = unlimited ? 0 : pool.limit > 0 ? Math.round((pool.used / pool.limit) * 100) : 0;

  const outstanding = stats.submitted.total + stats.queried.total;
  const outstandingCount = stats.submitted.count + stats.queried.count;

  const donutData = [
    { label: "To review", value: stats.submitted.count, color: STATUS_COLORS.submitted },
    { label: "Queried", value: stats.queried.count, color: STATUS_COLORS.queried },
    { label: "Paid", value: stats.paid.count, color: STATUS_COLORS.paid },
    { label: "Rejected", value: stats.rejected.count, color: STATUS_COLORS.rejected },
  ];
  const totalClaims = donutData.reduce((s, d) => s + d.value, 0);

  const planName = (company.tier || "").replace("company_", "");

  // value-by-month bars (in rand)
  const valueBars = insights.months.map((m) => ({ label: m.label, value: Math.round(m.value), color: "#12B266" }));
  const monthValueTotal = insights.months.reduce((s, m) => s + m.value, 0);

  // combined team activity: claim value (by name) + scan usage (by member),
  // ranked by claim value, falling back to scan count
  const claimByName = {};
  insights.topClaimers.forEach((c) => { claimByName[c.name] = c.total; });
  const activity = members.map((m) => ({
    name: m.name || m.email || "Unknown",
    claimed: claimByName[m.name] || 0,
    scans: m.scan_count || 0,
  }));
  // include any claimer not in the member roster (edge case)
  insights.topClaimers.forEach((c) => {
    if (!activity.find((a) => a.name === c.name)) activity.push({ name: c.name, claimed: c.total, scans: 0 });
  });
  activity.sort((a, b) => (b.claimed - a.claimed) || (b.scans - a.scans));
  const topActivity = activity.slice(0, 6);

  // trend arrow
  const trend = insights.momDelta;
  const trendUp = trend > 0;
  const trendColor = trend === 0 ? "text-tb-muted" : trendUp ? "text-green-600 dark:text-green-400" : "text-red-500";
  const trendSign = trend > 0 ? "+" : "";

  return (
    <div className="space-y-6">
      {/* header */}
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-display font-bold">Dashboard</h1>
          <p className="text-sm text-tb-muted mt-0.5">{company.name}</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="px-2.5 py-1 rounded-lg bg-tb-brand/10 text-tb-brand font-semibold capitalize">{planName} plan</span>
          <span className="px-2.5 py-1 rounded-lg bg-green-500/10 text-green-600 dark:text-green-400 font-semibold capitalize">{company.status}</span>
        </div>
      </div>

      {/* hero row: scan pool (wide) + outstanding */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Glass className="lg:col-span-2 p-6 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-tb-brand/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row items-center gap-6">
            <div className="flex-1 w-full text-center sm:text-left">
              <p className="text-xs font-semibold text-tb-muted uppercase tracking-wider">Scan pool</p>
              <p className="text-4xl font-bold mt-2" style={{ color: gaugeColor }}>
                {unlimited ? "\u221E" : pool.remaining}
                <span className="text-base font-medium text-tb-muted ml-2">{unlimited ? "unlimited" : "remaining"}</span>
              </p>
              <p className="text-sm text-tb-muted mt-1">
                {pool.used || 0} used{!unlimited && ` of ${pool.limit}`}
                {!unlimited && ` · ${usedPct}%`}
                {pool.credits > 0 && ` · +${pool.credits} credits`}
              </p>
              {!unlimited && (
                <div className="mt-4 h-2.5 rounded-full bg-tb-bg-2 dark:bg-tb-d-inset overflow-hidden">
                  <div className="h-full rounded-full tb-grow" style={{ width: `${usedPct}%`, background: `linear-gradient(to right, ${gaugeColor}, ${gaugeColor}cc)` }} />
                </div>
              )}
            </div>
            <div className="w-full sm:w-64 shrink-0">
              <GaugeChart
                value={unlimited ? 1 : (pool.remaining || 0)}
                max={unlimited ? 1 : pool.limit}
                centerValue={unlimited ? "\u221E" : pool.remaining}
                centerLabel={unlimited ? "unlimited" : "left"}
                color={gaugeColor}
              />
            </div>
          </div>
        </Glass>

        <Kpi
          icon={Hourglass}
          tint="#F59E0B"
          label="Outstanding"
          value={money(outstanding)}
          sub={`${outstandingCount} claim${outstandingCount !== 1 ? "s" : ""} awaiting action`}
          onClick={() => navigate("/company/claims?status=submitted")}
        />
      </div>

      {/* KPI strip - 3 insight metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <Kpi
          icon={Banknote}
          tint="#12B266"
          label="Paid out"
          value={money(stats.paid.total)}
          valueColor="text-green-600 dark:text-green-400"
          sub={`${stats.paid.count} settled`}
          onClick={() => navigate("/company/claims?status=paid")}
        />
        <Kpi
          icon={BadgeCheck}
          tint="#3B82F6"
          label="Approval rate"
          value={insights.approvalRate == null ? "\u2014" : `${insights.approvalRate}%`}
          sub={insights.decidedCount > 0 ? `of ${insights.decidedCount} decided` : "no decisions yet"}
          onClick={() => navigate("/company/claims?status=paid")}
        />
        <Kpi
          icon={TrendingUp}
          tint="#7C5CFC"
          label="Avg claim value"
          value={money(insights.avgClaimValue)}
          sub={<span className={trendColor}>{trend === 0 ? "no change vs last month" : `${trendSign}${trend}% vs last month`}</span>}
          onClick={() => navigate("/company/claims")}
        />
      </div>

      {/* claims by status + top claimers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Glass className="p-6">
          <p className="text-sm font-semibold mb-5">Claims by status</p>
          {totalClaims === 0 ? (
            <p className="text-sm text-tb-muted py-10 text-center">No claims yet.</p>
          ) : (
            <DonutChart data={donutData} size={150} thickness={20} center={{ value: totalClaims, label: "total" }} />
          )}
        </Glass>

        <button onClick={() => navigate("/company/codes")} className="block w-full text-left transition-transform hover:-translate-y-0.5">
          <Glass className="p-6 h-full">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-semibold">Team efficiency</p>
              <span className="text-xs text-tb-muted">scans vs claim value</span>
            </div>
            {activity.length === 0 ? (
              <p className="text-sm text-tb-muted py-10 text-center">No members yet.</p>
            ) : activity.length >= 4 ? (
              <TeamScatter people={activity} />
            ) : (
              <TeamList people={topActivity} claimMax={Math.max(1, ...topActivity.map((a) => a.claimed))} scanMax={Math.max(1, ...topActivity.map((a) => a.scans))} />
            )}
          </Glass>
        </button>
      </div>

      {/* value flowing through, by month */}
      <Glass className="p-6">
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm font-semibold">Claim value by month</p>
          <span className="text-xs text-tb-muted">last 12 months</span>
        </div>
        {monthValueTotal === 0 ? (
          <p className="text-sm text-tb-muted py-10 text-center">No claims in this period.</p>
        ) : (
          <BarChart data={valueBars} height={200} />
        )}
      </Glass>

      {/* company details */}
      <Glass className="p-6">
        <p className="text-sm font-semibold mb-5">Company details</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6 text-sm">
          {[
            { label: "Plan", value: planName, capitalize: true },
            { label: "Status", value: company.status, capitalize: true },
            company.starts_at && { label: "Term start", value: new Date(company.starts_at).toLocaleDateString() },
            company.expires_at && { label: "Term end", value: new Date(company.expires_at).toLocaleDateString() },
            { label: "Team members", value: teamCount ?? "..." },
          ].filter(Boolean).map((row) => (
            <div key={row.label}>
              <p className="text-xs text-tb-muted uppercase tracking-wider mb-1">{row.label}</p>
              <p className={`font-medium ${row.capitalize ? "capitalize" : ""}`}>{row.value}</p>
            </div>
          ))}
        </div>
      </Glass>
    </div>
  );
}

// ranked list for small teams: avatar, claim value bar, scan count.
function TeamList({ people, claimMax, scanMax }) {
  const initials = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  const fmtK = (v) => v >= 1000 ? `R${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `R${Math.round(v)}`;
  return (
    <div className="space-y-3.5">
      {people.map((a) => {
        // flag a member who scans a lot relative to the team but claims little
        const watch = a.scans > scanMax / 2 && a.claimed < claimMax / 2;
        return (
          <div key={a.name} className="flex items-center gap-3 tb-fade-up" style={{ animationDelay: `${people.indexOf(a) * 0.08}s` }}>
            <div className={`w-9 h-9 rounded-full shrink-0 grid place-items-center text-xs font-semibold ${watch ? "text-red-500 bg-red-500/10" : "text-tb-brand bg-tb-brand/10"}`}>
              {initials(a.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="truncate">{a.name}</span>
                <span className="font-semibold tabular-nums ml-3 shrink-0">{money(a.claimed)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-tb-bg-2 dark:bg-tb-d-inset overflow-hidden">
                <div className="h-full rounded-full tb-grow" style={{ width: `${Math.max(a.claimed ? 4 : 0, Math.round((a.claimed / claimMax) * 100))}%`, background: "linear-gradient(to right, #12B266, #5fd99a)" }} />
              </div>
              <p className="text-[11px] text-tb-muted mt-1">{a.scans} scan{a.scans !== 1 ? "s" : ""}{watch ? " · high usage, low claims" : ""}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// scatter of members: x = scans, y = claim value. Top-right = productive,
// bottom-right = scanning a lot but claiming little (the waste signal).
function TeamScatter({ people }) {
  const W = 460, H = 240;
  const padL = 44, padR = 16, padT = 16, padB = 34;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const maxScans = Math.max(1, ...people.map((p) => p.scans));
  const maxClaim = Math.max(1, ...people.map((p) => p.claimed));
  const x = (scans) => padL + (scans / maxScans) * plotW;
  const y = (claim) => padT + plotH - (claim / maxClaim) * plotH;

  const initials = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  const fmtK = (v) => v >= 1000 ? `R${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `R${Math.round(v)}`;

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 260 }}>
        {/* quadrant shading: bottom-right (high scans, low claims) = watch zone */}
        <rect x={padL + plotW / 2} y={padT + plotH / 2} width={plotW / 2} height={plotH / 2}
              fill="#EF4444" opacity="0.06" />
        {/* top-right (high scans, high claims) = star zone */}
        <rect x={padL + plotW / 2} y={padT} width={plotW / 2} height={plotH / 2}
              fill="#12B266" opacity="0.06" />

        {/* axes */}
        <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="currentColor" strokeOpacity="0.15" />
        <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="currentColor" strokeOpacity="0.15" />

        {/* midlines */}
        <line x1={padL + plotW / 2} y1={padT} x2={padL + plotW / 2} y2={padT + plotH}
              stroke="currentColor" strokeOpacity="0.08" strokeDasharray="3 3" />
        <line x1={padL} y1={padT + plotH / 2} x2={padL + plotW} y2={padT + plotH / 2}
              stroke="currentColor" strokeOpacity="0.08" strokeDasharray="3 3" />

        {/* axis labels */}
        <text x={padL + plotW} y={padT + plotH + 22} textAnchor="end" fontSize="10" fill="currentColor" fillOpacity="0.5">more scans →</text>
        <text x={padL - 6} y={padT + 8} textAnchor="end" fontSize="10" fill="currentColor" fillOpacity="0.5">value</text>

        {/* points */}
        {people.map((p, i) => {
          const cx = x(p.scans);
          const cy = y(p.claimed);
          // watch = scans above median-ish and claims below
          const watch = p.scans > maxScans / 2 && p.claimed < maxClaim / 2;
          const color = watch ? "#EF4444" : "#12B266";
          return (
            <g key={i} className="tb-pop" style={{ animationDelay: `${0.2 + i * 0.1}s`, transformOrigin: `${cx}px ${cy}px` }}>
              <circle cx={cx} cy={cy} r="14" fill={color} fillOpacity="0.15" />
              <circle cx={cx} cy={cy} r="11" fill={color} fillOpacity="0.9" />
              <text x={cx} y={cy + 3} textAnchor="middle" fontSize="9" fontWeight="600" fill="#fff">{initials(p.name)}</text>
              <text x={cx} y={cy - 16} textAnchor="middle" fontSize="9" fill="currentColor" fillOpacity="0.6">{fmtK(p.claimed)} · {p.scans}s</text>
            </g>
          );
        })}
      </svg>

      {/* legend */}
      <div className="flex items-center justify-center gap-5 mt-2 text-[11px] text-tb-muted">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-tb-brand" /> productive</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> high scans, low claims</span>
      </div>
    </div>
  );
}

// derive all analytics from the fetched rows (no backend change)
function computeInsights(map, all) {
  const paid = map.paid.rows;
  const rejected = map.rejected.rows;

  // approval rate: paid / (paid + rejected)
  const decidedCount = paid.length + rejected.length;
  const approvalRate = decidedCount > 0 ? Math.round((paid.length / decidedCount) * 100) : null;

  // avg payout time: days from submitted_at -> paid_at on paid claims
  let dSum = 0, dN = 0;
  paid.forEach((c) => {
    if (c.submitted_at && c.paid_at) {
      const days = (new Date(c.paid_at) - new Date(c.submitted_at)) / 86400000;
      if (days >= 0) { dSum += days; dN += 1; }
    }
  });
  const avgPayoutDays = dN > 0 ? Math.max(0, Math.round(dSum / dN)) : null;

  // avg claim value across all claims
  const grandTotal = all.reduce((s, c) => s + Number(c.total || 0), 0);
  const avgClaimValue = all.length > 0 ? grandTotal / all.length : 0;

  // value + count per month (last 6)
  const months = monthBuckets();
  const idx = {};
  months.forEach((m, i) => { idx[m.key] = i; });
  const monthClaimDate = (c) => c.submitted_at || c.created_at;
  all.forEach((c) => {
    const raw = monthClaimDate(c);
    if (!raw) return;
    const d = new Date(raw);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key in idx) { months[idx[key]].count += 1; months[idx[key]].value += Number(c.total || 0); }
  });

  // month-over-month: this month value vs previous month
  const thisM = months[11]?.value || 0;
  const prevM = months[10]?.value || 0;
  const momDelta = prevM > 0 ? Math.round(((thisM - prevM) / prevM) * 100) : (thisM > 0 ? 100 : 0);

  // top claimers by total value
  const byPerson = {};
  all.forEach((c) => {
    const name = c.submitter_name || c.submitter_email || "Unknown";
    if (!byPerson[name]) byPerson[name] = { name, total: 0, count: 0 };
    byPerson[name].total += Number(c.total || 0);
    byPerson[name].count += 1;
  });
  const topClaimers = Object.values(byPerson).sort((a, b) => b.total - a.total).slice(0, 5);

  return {
    approvalRate, decidedCount,
    avgPayoutDays, paidWithTime: dN,
    avgClaimValue, momDelta,
    months, topClaimers,
  };
}